import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, esCuentaEnDolares, hoy, newId, parseMoney, type CuentaBanco } from '@abastece/shared';
import { readSettings } from '../db/reader.js';
import { cuentaQueQueda, delDuenoEnEfectivo, delDuenoPorBanco } from '../lib/bancos.js';
import { emitirYVer } from '../lib/emitir.js';
import { FORMAS_GASTO, type FormaGasto } from '../lib/gastos.js';
import { useT } from '../i18n/index.js';
import { Campo, Selector } from './Campo.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

type Destino = Exclude<FormaGasto, 'credito'>;

/**
 * «Aporte del dueño»: money the owner puts into the business, which raises its capital (the owner,
 * 2026-10-07: «need a way to put in owner's money to increase equity»). The same record «Gastos y
 * pagos»' «Dueño» tab writes when the owner puts money in (`dueno.aporte`): the cash, the bank or
 * the wallet up and «Capital» up by as much, no income and no debt. Listed, and voided, there. By
 * transfer, into any of the shop's accounts: a dollar account's in its dollars.
 */
export function AporteDelDueno({ onCerrar }: { onCerrar: () => void }) {
  const t = useT();
  const [monto, setMonto] = useState('');
  const [destino, setDestino] = useState<Destino>('efectivo_cordobas');
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBanco | null>(null);
  const cuenta = cuentaQueQueda(config, cuentaElegida, 'USD');
  const enDolares = (destino === 'transferencia' && esCuentaEnDolares(cuenta)) || destino === 'efectivo_dolares';
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valor = parseMoney(monto);
  const valido = valor !== null && valor > 0;

  async function guardar() {
    if (!valido || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('dueno.aporte', {
        movimientoId: newId(),
        fecha: hoy(),
        destino,
        ...(destino === 'transferencia' ? delDuenoPorBanco(config, cuenta, valor!) : delDuenoEnEfectivo(config, destino, valor!)),
        ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
      });
      if (rechazo) setError(rechazo);
      else onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      titulo={t('aporte.titulo')}
      onCerrar={onCerrar}
      pie={
        <button type="button" className="btn-primario w-full" disabled={!valido || guardando} onClick={() => void guardar()}>
          <Ico icono="guardar" />
          {t('aporte.guardar')}
        </button>
      }
    >
      <div className="flex flex-col gap-4" data-aporte-dueno>
        <Campo
          etiqueta={t(enDolares ? 'gastos.montoDolares' : 'gastos.montoCordobas')}
          inputMode="decimal"
          className="tabular text-lg"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          placeholder="0.00"
          autoComplete="off"
          autoFocus
          error={monto.trim() && !valido ? t('caja.montoInvalido') : null}
        />
        <Selector etiqueta={t('gastos.aDondeEntra')} value={destino} onChange={(e) => setDestino(e.target.value as Destino)}>
          {FORMAS_GASTO.filter((f) => f.valor !== 'credito').map((f) => (
            <option key={f.valor} value={f.valor}>
              {t(f.nombre)}
            </option>
          ))}
        </Selector>
        {destino === 'transferencia' && (
          <ElegirCuenta moneda="USD" valor={cuenta} onCambiar={setCuentaElegida} etiqueta={t('bancos.entraA')} />
        )}
        <Campo etiqueta={t('aporte.motivo')} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={200} autoComplete="off" />
        {error && <p className="text-sm text-rose-400">{error}</p>}
        <p className="text-xs text-slate-500">{t('aporte.explicacion')}</p>
      </div>
    </Modal>
  );
}
